import type {
  AddToOrgDto,
  CognitoUserDataDto,
  JoinOrgRequestDto,
  RegisterUserDto,
  UserDataDto,
} from "@strtmn/sds/types"
import { UserErrorCodes } from "@strtmn/sds/schemas"
import type { UserOrgsRolesDto } from "@strtmn/sds/users"
import { Cache } from "aws-amplify/utils"
import { isDev, isProd } from "../config"
import { isUserSignedIn } from "../Utils.AmplifyAuth"
import { clearCacheOrgData } from "./OrgsApi"
import { getCacheKey, getFromApi, postToApi, putToApi } from "./utils"

// 10 / 100 minutes
const EXPIRES_IN_MS = (isDev ? 100 : 10) * 60 * 1000

/**
 * Thrown by `getCurrentUserData` if the signed-in Cognito user has not
 * registered yet, i.e. has no local user. Never cached.
 */
export class UserNotRegisteredError extends Error {
  constructor() {
    super("User not registered")
    this.name = "UserNotRegisteredError"
  }
}

export const getCurrentUserData = async (
  ignoreCache = false
): Promise<UserDataDto | false> => {
  console.log(`> getCurrentUserData(ignoreCache=${ignoreCache})`)
  // const fakeUserId = '3ee41fa1-7db2-44b8-929f-7c6d835b2eda'
  // console.log(`getCurrentUserData: faking user ${fakeUserId}`)
  // return getById(fakeUserId)
  const apiName = "frontendApi"
  const path = "/users/current"
  const cacheKey = getCacheKey(apiName, path)

  const currentUser = await isUserSignedIn()
  if (!currentUser) {
    return false
  }
  !isProd && console.debug("getCurrentUserData: user signed in. Let's go")

  try {
    // !isProd && console.debug("getCurrentUserData: try to get item from cache")
    if (ignoreCache) {
      console.log(
        `getCurrentUserData: remove item from cache (ignoreCache=${ignoreCache})`
      )
      await Cache.removeItem(cacheKey)
    }
    let data = await Cache.getItem(cacheKey)
    if (!data || !data.id || data.id !== currentUser.userId) {
      console.log("getCurrentUserData: userId mismatch -> clearing cache")
      if (!isProd) {
        console.log(
          `getCurrentUserData: current user = ${currentUser.userId} / cached = ${data?.id || "null"}`
        )
      }
      await Cache.clear()
      data = undefined
      /** @todo: Should this also trigger a clear of UserData, UserPreferences, and LocalStorage? */
    }
    if (data) {
      !isProd &&
        console.debug(
          "getCurrentUserData: Found in cache:",
          data,
          `${cacheKey}`
        )
    } else {
      console.log(
        "getCurrentUserData:",
        cacheKey,
        "not found in cache. Loading from API..."
      )
      try {
        data = (await getFromApi(apiName, path)).json
      } catch (err) {
        if ((err as any)?.body?.code === UserErrorCodes.NotRegistered) {
          throw new UserNotRegisteredError()
        }
        throw err
      }
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      !isProd && console.debug("getCurrentUserData: Fetched from api:", data)
    }
    // !isProd && console.debug("✅ getCurrentUserData: data")
    return data
  } catch (err) {
    console.error(
      "getCurrentUserData: Error fetching user data from cache (unlikely) or api (likely)."
    )
    console.log("getCurrentUserData: Error:", err)
    throw err
  }
}

/**
 * Self-service registration: creates the current user and its own org.
 * @returns the id of the created org, or `false` if not signed in
 * @throws the API error, e.g. `statusCode` 409 if already registered
 */
export const registerCurrentUser = async (
  user: RegisterUserDto
): Promise<string | false> => {
  console.log(`> registerCurrentUser()`)
  if (!(await isUserSignedIn())) {
    return false
  }
  let orgId: string
  try {
    const { json } = await postToApi("frontendApi", "/users/current", user)
    orgId = (json as { orgId: string }).orgId
  } catch (err) {
    console.error(`registerCurrentUser: Error registering.`)
    console.log("registerCurrentUser: Error:", err)
    throw err
  }
  await clearCacheCurrentUserData()
  return orgId
}

/**
 * Self-service registration, asking to join an existing org. Customer support
 * is informed and adds the user to the org.
 * @throws the API error, e.g. `statusCode` 409 if already registered
 */
export const requestJoinOrg = async (
  request: JoinOrgRequestDto
): Promise<boolean> => {
  console.log(`> requestJoinOrg()`)
  if (!(await isUserSignedIn())) {
    return false
  }
  try {
    await postToApi("frontendApi", "/users/current/join-request", request)
  } catch (err) {
    console.error(`requestJoinOrg: Error sending the request.`)
    console.log("requestJoinOrg: Error:", err)
    throw err
  }
  await clearCacheCurrentUserData()
  return true
}

export const createUser = async (
  user: UserDataDto
): Promise<boolean | false> => {
  console.log(`> createUser(${user.id})`)
  const apiName = "frontendApi"
  const path = `/users/${user.id}`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await putToApi(apiName, path, user)
    !isProd && console.debug("✅ createUser: api response:", response)
  } catch (err) {
    console.error(`createUser: Error saving user ${user.id}.`)
    console.log("createUser: Error:", err)
    throw err
  }
  await clearCacheUserData(user.id)
  await clearCacheListCognitoUsers()
  await clearCacheListUsers()
  return true
}

export const updateUser = async (
  user: UserDataDto
): Promise<boolean | false> => {
  console.log(`> updateUser(${user.id})`)
  const apiName = "frontendApi"
  const path = `/users/${user.id}`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await putToApi(apiName, path, user)
    !isProd && console.debug("✅ updateUser: api response:", response)
  } catch (err) {
    console.error(`updateUser: Error saving user ${user.id}.`)
    console.log("updateUser: Error:", err)
    // !isProd && console.debug(`updateUser: user =`, user)
    throw err
  }
  await clearCacheUserData(user.id)
  return true
}

export const addUserToOrg = async (
  userId: string,
  orgId: string,
  orgRole: UserOrgsRolesDto
): Promise<boolean | false> => {
  console.log(`> addUserToOrg(${userId}, ${orgId}, ${orgRole})`)
  const apiName = "frontendApi"
  const path = `/users/${userId}/orgs`
  const body: AddToOrgDto = { orgId, orgRole }

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await postToApi(apiName, path, body)
    !isProd && console.debug("✅ addUserToOrg: api response:", response)
  } catch (err) {
    console.error(`addUserToOrg: Error adding user ${userId} to org ${orgId}.`)
    console.log("addUserToOrg: Error:", err)
    throw err
  }
  await clearCacheUserData(userId)
  await clearCacheOrgData(orgId)
  return true
}

export const listCognitoUsers = async (): Promise<
  CognitoUserDataDto[] | false
> => {
  console.log(`> listCognitoUsers()`)
  const apiName = "frontendApi"
  const path = `/users/cognito`
  const cacheKey = getCacheKey(apiName, path)

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    // !isProd && console.debug("try to get item from cache")
    // console.warn("remove item from cache") // DBG
    // await Cache.removeItem(cacheKey) // DBG
    let data: CognitoUserDataDto[] | null = await Cache.getItem(cacheKey)
    if (data) {
      console.log("listCognitoUsers: Found in cache:", data, `${cacheKey}`)
    } else {
      console.log(
        "listCognitoUsers:",
        cacheKey,
        "not found in cache. Loading from API..."
      )
      data = (await getFromApi(apiName, path)).json as CognitoUserDataDto[]
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      !isProd && console.debug("listCognitoUsers: Fetched from api:", data)
    }
    // !isProd && console.debug("✅ listCognitoUsers: data")
    return data
  } catch (err) {
    console.error(
      "listCognitoUsers: Error fetching Users data from cache (unlikely) or api (likely)."
    )
    console.log("listCognitoUsers: Error:", err)
    throw err
  }
}

export const listUsers = async (): Promise<UserDataDto[] | false> => {
  console.log(`> listUsers()`)
  const apiName = "frontendApi"
  const path = `/users`
  const cacheKey = getCacheKey(apiName, path)

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    // !isProd && console.debug("try to get item from cache")
    // console.warn("remove item from cache") // DBG
    // await Cache.removeItem(cacheKey) // DBG
    let data: UserDataDto[] | null = await Cache.getItem(cacheKey)
    if (data) {
      console.log("listUsers: Found in cache:", data, `${cacheKey}`)
    } else {
      console.log(
        "listUsers:",
        cacheKey,
        "not found in cache. Loading from API..."
      )
      data = (await getFromApi(apiName, path)).json as UserDataDto[]
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      !isProd && console.debug("listUsers: Fetched from api:", data)
    }
    // !isProd && console.debug("✅ listUsers: data")
    return data
  } catch (err) {
    console.error(
      "listUsers: Error fetching Users data from cache (unlikely) or api (likely)."
    )
    console.log("listUsers: Error:", err)
    throw err
  }
}

export const getById = async (userId: string): Promise<UserDataDto | false> => {
  console.log(`> getById()`)
  const apiName = "frontendApi"
  const path = `/users/${userId}`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    console.log("getById: Uncached. Loading from API...")
    let data: UserDataDto | null = (await getFromApi(apiName, path))
      .json as UserDataDto
    !isProd && console.debug("getById: Fetched from api:", data)
    // !isProd && console.debug("✅ getById: data")
    return data
  } catch (err) {
    console.error("getById: Error fetching User data from api.")
    console.log("getById: Error:", err)
    throw err
  }
}

export const sortUsers = (users: UserDataDto[]) => {
  console.debug(`> sortUsers (${users.length} orgs)`)
  const pushU1ToBack = 1
  const pushU2ToBack = -1
  users.sort((u1: UserDataDto, u2: UserDataDto) => {
    // Sort by last_name
    if (!u1.last_name && !u2.last_name) {
      return 0
    }
    if (!u1.last_name && u2.last_name) {
      return pushU1ToBack
    }
    if (u1.last_name && !u2.last_name) {
      return pushU2ToBack
    }
    if (u1.last_name! < u2.last_name!) {
      return pushU2ToBack
    }
    if (u1.last_name! > u2.last_name!) {
      return pushU1ToBack
    }

    return 0
  })
  return users
}

export const filterUsers = (
  users: UserDataDto[],
  filter: string | undefined
) => {
  if (!filter || !users) {
    return users
  }
  return users.filter((user) => {
    const haystack = [user.first_name, user.last_name, user.email]
      .join("#")
      .toLowerCase()
    return haystack.includes(filter)
  })
}

export const clearCacheCurrentUserData = async (): Promise<void> => {
  console.debug(`> clearCacheCurrentUserData`)
  const apiName = "frontendApi"
  const path = `/users/current`
  const cacheKey = getCacheKey(apiName, path)
  !isProd && console.debug("Remove item from cache:", cacheKey)
  await Cache.removeItem(cacheKey)
}

const clearCacheUserData = async (userId: string): Promise<void> => {
  console.debug(`> clearCacheUserData(${userId})`)
  const apiName = "frontendApi"
  const path = `/users/${userId}`
  const cacheKey = getCacheKey(apiName, path)
  !isProd && console.debug("Remove item from cache:", cacheKey)
  await Cache.removeItem(cacheKey)
}

const clearCacheListUsers = async (): Promise<void> => {
  console.debug(`> clearCacheListUsers()`)
  const apiName = "frontendApi"
  const path = `/users`
  const cacheKey = getCacheKey(apiName, path)
  !isProd && console.debug("Remove item from cache:", cacheKey)
  await Cache.removeItem(cacheKey)
}

const clearCacheListCognitoUsers = async (): Promise<void> => {
  console.debug(`> clearCacheListCognitoUsers()`)
  const apiName = "frontendApi"
  const path = `/users/cognito`
  const cacheKey = getCacheKey(apiName, path)
  !isProd && console.debug("Remove item from cache:", cacheKey)
  await Cache.removeItem(cacheKey)
}
